import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import api from "../api";
import { useLang, fmtPrice } from "../i18n";
import Marquee from "../components/Marquee";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.25 + i * 0.14, duration: 1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Home() {
  const { t, loc, lang } = useLang();
  const [featured, setFeatured] = useState([]);
  const [quote, setQuote] = useState("");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgR = useTransform(scrollYProgress, [0, 1], [-2, 3]);

  useEffect(() => {
    api.get("/artworks").then((r) => setFeatured(r.data.filter((w) => w.featured))).catch(() => {});
    api.get("/content").then((r) => setQuote(lang === "fr" ? r.data.quote_fr : r.data.quote_en)).catch(() => {});
  }, [lang]);

  const spans = [
    "sm:col-span-3 sm:row-span-2",
    "sm:col-span-3 sm:row-span-1",
    "sm:col-span-2 sm:row-span-1",
    "sm:col-span-2 sm:row-span-2",
    "sm:col-span-2 sm:row-span-1",
    "sm:col-span-6 sm:row-span-1",
  ];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-16">
        <div className="px-6 sm:px-12 lg:px-20 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center py-20">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-8"
              data-testid="hero-kicker"
            >
              {t("hero.kicker")}
            </motion.p>
            <h1 className="font-serif-display font-light leading-[0.95] tracking-tight" data-testid="hero-title">
              <span className="block overflow-hidden">
                <motion.span
                  custom={0}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  className="block text-[16vw] lg:text-[9.5rem]"
                >
                  {t("hero.line1")}
                </motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span
                  custom={1}
                  variants={lineReveal}
                  initial="hidden"
                  animate="show"
                  className="block italic text-[8vw] lg:text-6xl text-[#5A5853]"
                >
                  — {t("hero.line2")}
                </motion.span>
              </span>
            </h1>
            <motion.p
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ delay: 0.8 }}
              className="mt-10 max-w-md text-base text-[#5A5853] leading-relaxed"
              data-testid="hero-sub"
            >
              {t("hero.sub")}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/oeuvres"
                data-testid="hero-cta-works"
                className="group inline-flex items-center gap-3 px-8 py-4 font-mono-meta text-xs uppercase tracking-[0.2em] text-[#FAF9F6] transition-colors"
                style={{ background: "#121212" }}
              >
                {t("hero.cta_works")}
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/presentation"
                data-testid="hero-cta-about"
                className="inline-flex items-center gap-3 px-8 py-4 font-mono-meta text-xs uppercase tracking-[0.2em] border border-[#121212] hover:bg-[#121212] hover:text-[#FAF9F6] transition-colors"
              >
                {t("hero.cta_about")}
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, y: 60, rotate: 4 }}
              animate={{ opacity: 1, y: 0, rotate: -2 }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              style={{ y: imgY, rotate: imgR }}
              className="relative mx-auto w-4/5 lg:w-full max-w-md"
            >
              <div className="float-slow">
                <div className="p-3 bg-white frame-shadow" data-testid="hero-artwork-frame">
                  <img
                    src="/artworks/p10.jpg"
                    alt="Substances émanantes — Max Dhum"
                    className="w-full aspect-[4/5] object-cover"
                  />
                  <div className="flex justify-between items-center pt-3 px-1">
                    <span className="font-serif-display italic text-sm">Substances émanantes</span>
                    <span className="font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]">2026</span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="absolute -bottom-8 left-0 font-mono-meta text-[10px] uppercase tracking-[0.25em] text-[#8C8983]"
            >
              {t("hero.scroll")} ↓
            </motion.span>
          </div>
        </div>
      </section>

      <Marquee />

      {/* FEATURED BENTO */}
      <section className="px-6 sm:px-12 lg:px-20 py-24 lg:py-32" data-testid="featured-section">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="flex items-end justify-between mb-14"
        >
          <div>
            <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-4">
              {t("home.featured_kicker")}
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light">{t("home.featured_title")}</h2>
          </div>
          <Link
            to="/oeuvres"
            data-testid="featured-view-all"
            className="hidden sm:inline-flex items-center gap-2 font-mono-meta text-xs uppercase tracking-[0.2em] link-line"
          >
            {t("home.view_all")} <ArrowUpRight size={14} />
          </Link>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-6 auto-rows-[240px] gap-4">
          {featured.map((w, i) => (
            <motion.div
              key={w.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`group relative overflow-hidden ${spans[i % spans.length]}`}
            >
              <Link to={`/oeuvres/${w.slug}`} data-testid={`featured-work-${w.slug}`} className="block w-full h-full">
                <img
                  src={w.image}
                  alt={loc(w, "title")}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6"
                  style={{ background: "linear-gradient(to top, rgba(18,18,18,0.75), transparent 60%)" }}
                >
                  <p className="font-serif-display italic text-2xl text-[#FAF9F6]">{loc(w, "title")}</p>
                  <p className="font-mono-meta text-[10px] uppercase tracking-[0.18em] text-[#FAF9F6]/70 mt-1">
                    {loc(w, "technique")} · {w.dimensions}{w.status !== "sold" && ` · ${fmtPrice(w.price_eur, lang)}`}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* QUOTE */}
      <section className="border-y" style={{ borderColor: "#E5E2DC", background: "#141414" }} data-testid="quote-section">
        <div className="px-6 sm:px-12 lg:px-20 py-24 lg:py-36 max-w-5xl">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#B39359] mb-10"
          >
            {t("home.quote_kicker")}
          </motion.p>
          <motion.blockquote
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="font-serif-display font-light italic text-3xl sm:text-4xl lg:text-5xl leading-snug text-[#FAF9F6]"
            data-testid="home-quote"
          >
            «&nbsp;{quote}&nbsp;»
          </motion.blockquote>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="mt-8 font-mono-meta text-xs uppercase tracking-[0.2em] text-[#8C8983]"
          >
            — Max Dhum, Leftovers 2026
          </motion.p>
        </div>
      </section>

      {/* ACQUIRE MODEL */}
      <section className="px-6 sm:px-12 lg:px-20 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="acquire-intro">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="lg:col-span-5"
        >
          <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-4">
            {t("home.acquire_kicker")}
          </p>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-light leading-tight">{t("home.acquire_title")}</h2>
        </motion.div>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end"
        >
          <p className="text-base text-[#5A5853] leading-relaxed mb-8">{t("home.acquire_text")}</p>
          <div className="flex gap-8 font-mono-meta text-xs uppercase tracking-[0.18em]">
            {[t("acquire.step1_t"), t("acquire.step2_t"), t("acquire.step3_t")].map((s) => (
              <span key={s} className="border-t border-[#121212] pt-3 flex-1">
                {s}
              </span>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  );
}
